import React from 'react';

import {
  Button,
  Grid,
  Menu,
  MenuItem
} from 'e100-react-components';
import {
  Box,
  Typography
} from 'shared/components';
import { Icon } from 'components';
import { FavouriteStationsAddSnackbar } from 'features/stations';

import {
  ACTIVE_BUTTON_ICON,
  BUTTON_ICON
} from '../../constants';

import { useStationInfo } from 'store/features/station';
import { useIsExistFavouriteBrandByCode } from 'entities/brands';
import { useIsExistFavouriteStationByStationCode } from 'entities/stations';
import { useIntl } from 'react-intl';
import {
  useShowMenu,
  useBoolState
} from 'common/hooks';
import { useStyles } from './styles';
import {
  useIsAddingFavouritStationsLoading,
  useIsDeletingFavouritStationsLoading
} from 'entities/stations';
import {
  useIsAddingFavouriteBrandsLoading,
  useIsDeletingFavouriteBrandsLoading
} from 'entities/brands';
import { useBrandByCode } from 'store/features/directories';
import {
  useShowFavouriteStationsActions,
  useDeleteFavouriteStation,
  useAddFavouriteStation,
  useDeleteFavouriteBrand,
  useAddFavouriteBrand
} from '../../hooks';

import { FavouriteStationsDeleteModal } from '../FavouriteStationsDeleteModal/FavouriteStationsDeleteModal';
import {
  FavouriteBrandsDeleteModal,
  FavouriteBrandsAddSnackbar
} from 'features/brands';

export const FavouriteStationsActions = () => {
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const { anchorEl, onShowMenu, onCloseMenu } = useShowMenu();
  const { code = '', brand } = useStationInfo() || {};
  const { code: brandCode } = brand || {};
  const { billingId } = useBrandByCode(brandCode) || {};
  const isExistFavouriteStation = useIsExistFavouriteStationByStationCode(code);
  const isExistFavouriteBrand = useIsExistFavouriteBrandByCode(brandCode);
  const addFavouriteStation = useAddFavouriteStation();
  const deleteFavouriteBrand = useDeleteFavouriteBrand();
  const [ showDeleteFavouriteStation, toggleDeleteFavouriteStation,, hideDeleteFavouriteStation ] = useBoolState();
  const [ showDeleteFavouriteBrand, toggleDeleteFavouriteBrand,, hideDeleteFavouriteBrand ] = useBoolState();
  const isAddingFavouritStationsLoading = useIsAddingFavouritStationsLoading();
  const isDeletingFavouritStationsLoading = useIsDeletingFavouritStationsLoading();
  const isAddingFavouriteBrandsLoading = useIsAddingFavouriteBrandsLoading();
  const isDeletingFavouriteBrandsLoading = useIsDeletingFavouriteBrandsLoading();
  const showFavouriteStationsActions = useShowFavouriteStationsActions();
  const deleteFavouriteStation = useDeleteFavouriteStation();
  const addFavouriteBrand = useAddFavouriteBrand();

  if (!showFavouriteStationsActions) {
    return null;
  }

  return (
    <>
      <Grid item>
        <Button
          variant='outlined'
          classes={{
            root: classes.buttonRoot
          }}
          onClick={onShowMenu}
        >
          <Icon
            kind={isExistFavouriteStation || isExistFavouriteBrand ? ACTIVE_BUTTON_ICON : BUTTON_ICON}
            color={'primary'}
          />
        </Button>
      </Grid>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={onCloseMenu}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <Box
          padding={{
            top: 1,
            right: 2,
            bottom: 1,
            left: 2
          }}
          classes={{
            root: classes.title
          }}
        >
          <Typography
            variant={'subtitle2'}
          >
            {formatMessage({ id: 'addToFavourite' })}
          </Typography>
        </Box>
        <MenuItem
          onClick={async () => {
            if (!isAddingFavouritStationsLoading && !isDeletingFavouritStationsLoading) {
              if (isExistFavouriteStation) {
                toggleDeleteFavouriteStation();
              } else {
                addFavouriteStation(code);
              }
            }
          }}
        >
          <Grid container justify="space-between" spacing={0} alignItems='center'>
            <Grid item>
              <Typography variant="body2">
                {formatMessage({
                  id: isExistFavouriteStation 
                    ? 'deleteServicePointFromFavourites'
                    : 'addOnlyThisServicePoint'
                })}
              </Typography>
            </Grid>
            <Grid item>
              {
                isExistFavouriteStation &&
                !isAddingFavouritStationsLoading &&
                !isDeletingFavouritStationsLoading && (
                  <Icon
                    kind="done"
                    color="primary"
                    classes={{
                      root: classes.iconRoot
                    }}
                  />
                )
              }
            </Grid>
          </Grid>
        </MenuItem>
        {
          brandCode && (
            <MenuItem
              onClick={async () => {
                if (!isAddingFavouriteBrandsLoading && !isDeletingFavouriteBrandsLoading) {
                  if (isExistFavouriteBrand) {
                    toggleDeleteFavouriteBrand();
                  } else {
                    addFavouriteBrand(billingId, brandCode);
                  }
                }
              }}
            >
              <Grid container justify="space-between" spacing={0} alignItems='center'>
                <Grid item>
                  <Typography
                    variant="body2"
                  >
                    {formatMessage({
                      id: isExistFavouriteBrand 
                        ? 'deleteBrandFromFavorites'
                        : 'addEntireBrand'
                    })}
                  </Typography>
                </Grid>
                <Grid item>
                  {
                    isExistFavouriteBrand &&
                    !isAddingFavouriteBrandsLoading &&
                    !isDeletingFavouriteBrandsLoading && (
                      <Icon
                        kind="done"
                        color="primary"
                        classes={{
                          root: classes.iconRoot
                        }}
                      />
                    )
                  }
                </Grid>
              </Grid>
            </MenuItem>
          )
        }
      </Menu>
      <FavouriteStationsDeleteModal
        open={showDeleteFavouriteStation}
        onOk={async () => {
          deleteFavouriteStation(code);
          hideDeleteFavouriteStation();
        }}
        onCancel={() => {
          hideDeleteFavouriteStation();
        }}
      />
      <FavouriteBrandsDeleteModal
        open={showDeleteFavouriteBrand}
        onOk={async () => {
          deleteFavouriteBrand(billingId, `${brandCode}`);
          hideDeleteFavouriteBrand();
        }}
        onCancel={() => {
          hideDeleteFavouriteBrand();
        }}
      />
      <FavouriteStationsAddSnackbar />
      <FavouriteBrandsAddSnackbar />
    </>
  );
};
